import { ChangeDetectorRef, Injectable, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import get from 'lodash-es/get';
import { from, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, shareReplay, tap } from 'rxjs/operators';

import { defaultLocale } from './app.constants';
import { LoggingService } from './logging.service';
// import { getLocalStorageSettings } from './profile/setting.utils';

export interface LocaleCopy {
  [key: string]: string | LocaleCopy
}

export class TranslationBundle {
  static EMPTY = new TranslationBundle({}, {} as LoggingService)

  constructor(readonly copy: LocaleCopy, private logger: LoggingService) { }

  get(key: string | string[], silent = false): string {
    if (!key) {
      return '';
    }

    key = Array.isArray(key) ? key : key.split('.');
    const message = get(this.copy, key) as string;
    if (typeof message !== 'string') {
      if (!silent && this.logger?.warn) {
        this.logger.warn(`Unable to find translation: ${key.join('.')}`);
      }
      return '';
    }
    return message;
  }
}

function getLocalStorageSettings(): { locale: string } {
  return {
    locale: defaultLocale,
  }
}

type ReplacementValue = string | number | boolean;

const warningTracker: { [template: string]: boolean } = {};

/**
 * Legacy service for localization
 * @deprecated Please just use plain text for copy, removal of references to this service is encouraged
 * @see if/when we adopt a localization strategy we will probably do so with the [Angular i18n lib](https://angular.dev/guide/i18n)
 */
@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  public locale = defaultLocale
  // FIXME: When we implement a user facing way to change the locale, we'll need to poll this on an interval
  // The method for setting the locale of copy requires modification, see FLYW-20000
  readonly copy$: Observable<LocaleCopy> = of(getLocalStorageSettings()).pipe(
    map(settings => settings.locale),
    distinctUntilChanged(),
    switchMap(locale =>
      from(import(`./locale/copy.${locale}.json`))
        .pipe(
          catchError(() => {
            this.logger.warn(`Locale is not defined: ${locale}`);
            return from(import('./locale/copy.en-US.json'));
          }),
        )
    ),
    tap(copy => {
      this.locale = copy.locale;
    }),
    shareReplay(1),
  )
  readonly bundle$: Observable<TranslationBundle> = this.copy$.pipe(map(copy => new TranslationBundle(copy, this.logger)))

  constructor(
    private logger: LoggingService,
  ) { }

  public getBundle(key?: string): Observable<TranslationBundle> {
    return this.copy$.pipe(
      map(copy => {
        if (key) {
          copy = get(copy, key) as LocaleCopy;
        }
        return new TranslationBundle(copy, this.logger);
      })
    );
  }

  public getMessage(key: string, silent = false): Observable<string> {
    return this.bundle$.pipe(map(bundle => bundle.get(key, silent)));
  }

  public interpolate(template: string, ...replacements: ReplacementValue[]): string {
    return String(template ?? '').replace(/{{([^}]+)}}/g, (val, key) => {
      if (!(key in replacements) && !(template in warningTracker)) {
        this.logger.warn(`Missing placeholder values for message "${template}"`);
        warningTracker[template] = true;
      }
      return String(replacements[key] ?? '');
    });
  }
}

/**
 * Translate a message given a nested key to a location in the loaded locale copy.
 * @deprecated Please just use plain text for copy, removal of references to this pipe are encouraged
 * @see if/when we adopt a localization strategy we will probably do so with the [Angular i18n lib](https://angular.dev/guide/i18n)
 */
@Pipe({
  name: 'translate',
  pure: false,
})
export class TranslatePipe implements PipeTransform, OnDestroy {
  private subscriptions: { [key: string]: Subscription } = {}
  private messages: { [key: string]: string } = {}

  constructor(
    private cdr: ChangeDetectorRef,
    private translationService: TranslationService,
  ) {}

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  /**
  * @deprecated Please just use plain text for copy, removal of references to this pipe are encouraged
  * @see if/when we adopt a localization strategy we will probably do so with the [Angular i18n lib](https://angular.dev/guide/i18n)
  */
  transform(key: string, silent = false): string {
    if (!(key in this.subscriptions)) {
      this.subscriptions[key] = this.translationService.getMessage(key, silent).subscribe(message => {
        this.messages[key] = message;
        // trigger change detection to support components with ChangeDetectionStrategy.OnPush
        this.cdr.markForCheck();
      });
    }
    return this.messages[key] || '';
  }
}

/**
 * Replace template placeholders in the form {{0}}, where the zero is the index of a value in the provided replacement array.
 * If not enough replacement values are provided for the given template, throw a warning once per template.
 * @deprecated Please just use plain text for copy, removal of references to this pipe are encouraged
 * @see if/when we adopt a localization strategy we will probably do so with the [Angular i18n lib](https://angular.dev/guide/i18n)
 */
@Pipe({
  name: 'interpolate',
  pure: true,
})
export class InterpolatePipe implements PipeTransform {
  constructor(private translationService: TranslationService) { }

  /**
  * @deprecated Please just use plain text for copy, removal of references to this pipe are encouraged
  * @see if/when we adopt a localization strategy we will probably do so with the [Angular i18n lib](https://angular.dev/guide/i18n)
  */
  transform(template: string, ...replacements: ReplacementValue[]): string {
    return this.translationService.interpolate(template, ...replacements);
  }
}
